import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import { Sparkles, ArrowRight, X, Loader2 } from "lucide-react";

/**
 * useAnnualNudgeStatus — polls the backend once for whether to show the
 * annual-upgrade nudge for the current user. Cached in-session so multiple
 * mount points (banner, toast, home card) share one round-trip.
 */
let _cachedPromise = null;
function useAnnualNudgeStatus() {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!_cachedPromise) {
      _cachedPromise = api
        .get("/subscriptions/annual-nudge/status")
        .then((r) => r.data)
        .catch(() => ({ show: false, reason: "error" }));
    }
    _cachedPromise.then(setData);
  }, []);
  return data;
}

async function acceptSwitch(navigate) {
  trackEvent("billing_annual_nudge_click", { action: "switch" });
  try {
    const { data } = await api.post("/subscriptions/switch-to-annual");
    toast.success(
      `Switched to annual — you're now saving about $${Math.round(
        data.savings_estimate || 0
      ).toLocaleString()} a year.`
    );
    _cachedPromise = null;
    // small delay lets Stripe webhook catch up before we refresh billing
    setTimeout(() => {
      navigate("/billing");
      window.location.reload();
    }, 900);
  } catch (e) {
    toast.error(
      e?.response?.data?.detail ||
        "Could not switch to annual — try again from Billing."
    );
  }
}

async function dismiss() {
  trackEvent("billing_annual_nudge_dismiss");
  try {
    await api.post("/subscriptions/annual-nudge/dismiss");
    _cachedPromise = null;
  } catch {
    /* no-op */
  }
}

/* ================= Banner (top of Billing dashboard) ================= */
export function AnnualUpgradeBanner() {
  const status = useAnnualNudgeStatus();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (status?.show) trackEvent("billing_annual_nudge_shown", { placement: "billing_banner" });
  }, [status?.show]);
  if (!status?.show || hidden) return null;
  const run = async () => {
    setBusy(true);
    await acceptSwitch(navigate);
    setBusy(false);
  };
  return (
    <div
      className="rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-400/10 via-emerald-400/[0.03] to-transparent p-5 flex items-start gap-4 flex-wrap"
      data-testid="annual-upgrade-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
        <Sparkles className="w-5 h-5 text-emerald-300" />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="font-display text-lg font-bold text-white">
          Switch to annual and pocket ~{status.savings_pct}%
        </div>
        <div className="text-sm text-slate-300 mt-1">
          You&apos;re currently on <b>{status.plan_name}</b> monthly. Move to annual and save about{" "}
          <b className="text-emerald-300">
            ${Math.round(status.savings_estimate).toLocaleString()}/yr
          </b>
          . Same plan, prorated instantly.
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => { setHidden(true); dismiss(); }}
          data-testid="annual-upgrade-dismiss-btn"
          className="text-xs text-slate-400 hover:text-white px-3 py-2"
        >
          Not now
        </button>
        <button
          onClick={run}
          disabled={busy}
          data-testid="annual-upgrade-accept-btn"
          className="rounded-full px-4 py-2 text-sm bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Switch to annual
        </button>
      </div>
    </div>
  );
}

/* ================= Dashboard card (first 30 days only) ================= */
export function AnnualUpgradeCard() {
  const status = useAnnualNudgeStatus();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (status?.show && status?.is_new_subscriber)
      trackEvent("billing_annual_nudge_shown", { placement: "dashboard_card" });
  }, [status?.show, status?.is_new_subscriber]);
  if (!status?.show || !status.is_new_subscriber || hidden) return null;
  const run = async () => {
    setBusy(true);
    await acceptSwitch(navigate);
    setBusy(false);
  };
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-400/[0.08] via-teal-500/[0.05] to-transparent p-6"
      data-testid="annual-upgrade-card"
    >
      <button
        onClick={() => { setHidden(true); dismiss(); }}
        data-testid="annual-upgrade-card-dismiss-btn"
        className="absolute top-3 right-3 text-slate-500 hover:text-white"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-5 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 text-slate-900" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            Save {status.savings_pct}%
          </div>
          <h3 className="font-display text-2xl font-bold text-white mt-1">
            Move to annual and pocket ${Math.round(status.savings_estimate).toLocaleString()}/yr
          </h3>
          <p className="text-sm text-slate-300 mt-2 max-w-xl">
            You just started on <b>{status.plan_name}</b>. Switch to annual before your next
            renewal and lock in the lower rate for a full year — Stripe prorates the swap
            instantly.
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              onClick={run}
              disabled={busy}
              data-testid="annual-upgrade-card-accept-btn"
              className="rounded-full px-5 py-2.5 text-sm bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Switch to annual now
            </button>
            <button
              onClick={() => navigate("/billing")}
              className="text-xs text-emerald-300 hover:text-emerald-100 px-3 py-2 inline-flex items-center gap-1"
            >
              View plan details <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= One-time toast (after login) ================= */
let _toastShownThisSession = false;
export function AnnualUpgradeToast() {
  const status = useAnnualNudgeStatus();
  const navigate = useNavigate();
  useEffect(() => {
    if (!status?.show || _toastShownThisSession) return;
    _toastShownThisSession = true;
    trackEvent("billing_annual_nudge_shown", { placement: "toast" });
    const id = toast(
      `Save ${status.savings_pct}% by switching ${status.plan_name} to annual`,
      {
        description: `~$${Math.round(status.savings_estimate).toLocaleString()} back a year.`,
        duration: 10000,
        action: {
          label: "See how",
          onClick: () => {
            trackEvent("billing_annual_nudge_click", { placement: "toast" });
            navigate("/billing");
            toast.dismiss(id);
          },
        },
      }
    );
  }, [status?.show, status?.savings_pct, status?.savings_estimate, status?.plan_name, navigate]);
  return null;
}
